
import { useState } from 'react';
import { useRestaurant } from '../contexts/RestaurantContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { User, Lock, MapPin, UserPlus, ArrowRight, Sparkles, Phone, AlertTriangle, RefreshCw, Check, X, Server, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/api';

export const LoginPage = () => {
  const { login, registerClient } = useRestaurant();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showWarning, setShowWarning] = useState(() => !localStorage.getItem('gf_warning_seen'));

  const dismissWarning = () => {
    localStorage.setItem('gf_warning_seen', '1');
    setShowWarning(false);
  };

  const [captcha, setCaptcha] = useState({ q: '', a: 0 });
  const [captchaInput, setCaptchaInput] = useState('');

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 9) + 1;
    const num2 = Math.floor(Math.random() * 9) + 1;
    setCaptcha({ q: `${num1} + ${num2}`, a: num1 + num2 });
    setCaptchaInput('');
  };

  useState(() => {
    generateCaptcha();
  });

  // Diagnostic State
  const [showDiag, setShowDiag] = useState(false);
  const [diagStatus, setDiagStatus] = useState<any>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagMessage, setDiagMessage] = useState('');

  const fetchDiagStatus = async () => {
    setDiagLoading(true);
    setDiagMessage('');
    try {
      const status = await apiService.getDbStatus();
      setDiagStatus(status);
      setShowDiag(true);
    } catch (err: any) {
      setDiagMessage(err.message || 'فشل جلب تفاصيل تشخيص قاعدة البيانات');
      setShowDiag(true);
    } finally {
      setDiagLoading(false);
    }
  };

  const handleReconnect = async () => {
    setDiagLoading(true);
    setDiagMessage('');
    try {
      const result = await apiService.reconnectDb();
      setDiagStatus(prev => ({ ...prev, ...result }));
      if (result.connected) {
        setDiagMessage('✅ تم الاتصال بقاعدة البيانات بنجاح! يمكنك الآن تسجيل الدخول.');
        setError('');
      } else {
        setDiagMessage('❌ فشل الاتصال: ' + (result.error || 'خطأ غير معروف'));
      }
    } catch (err: any) {
      setDiagMessage('❌ فشل الاتصال: ' + (err.message || 'خطأ في الشبكة'));
    } finally {
      setDiagLoading(false);
    }
  };

  // Client State
  const [isRegistering, setIsRegistering] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: '',
    username: '', // Phone number as username
    password: '',
    address: '',
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (parseInt(captchaInput) !== captcha.a) {
      setError('يرجى حل سؤال الأمان بشكل صحيح');
      generateCaptcha();
      return;
    }
    try {
      const success = await login(username, password);
      if (!success) {
        setError('اسم المستخدم أو كلمة المرور غير صحيحة');
      }
    } catch (err: any) {
      setError(err.message || 'اسم المستخدم أو كلمة المرور غير صحيحة');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-white p-4" dir="rtl">
      {/* Animated Background Mesh */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-10%] right-[-10%] w-[70%] h-[70%] bg-green-100/40 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[60%] h-[60%] bg-green-50/60 rounded-full blur-[100px]"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(34,197,94,0.03)_0%,transparent_70%)]"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="w-full max-w-lg z-10"
      >
        <Card className="border-none bg-white/40 backdrop-blur-2xl shadow-[0_32px_64px_-15px_rgba(0,0,0,0.1)] overflow-hidden rounded-[48px] border border-white/40">
          <CardContent className="p-8 md:p-12">
            <div className="text-center mb-10 space-y-4">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="relative inline-block"
              >
                <div className="absolute -inset-4 bg-green-400/20 blur-2xl rounded-full"></div>
                <img src="/logo.jpg" alt="Logo" className="relative w-24 h-24 md:w-28 md:h-28 object-contain rounded-3xl shadow-2xl shadow-green-200/50 border-4 border-white" />
              </motion.div>
              <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-gray-900 font-['Tajawal']">GOMBITA <span className="text-green-600">FOOD</span></h1>
              <p className="text-gray-500 font-bold text-sm">مرحباً بك في عالم المذاق المميز ✨</p>
            </div>

            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 text-red-600 text-sm font-bold text-center bg-red-50/80 backdrop-blur-sm p-4 rounded-2xl border border-red-100 flex flex-col items-center gap-2"
                >
                  <span>{error}</span>
                  {error.includes('قاعدة البيانات') && (
                    <button
                      type="button"
                      onClick={fetchDiagStatus}
                      disabled={diagLoading}
                      className="text-xs text-green-700 hover:text-green-900 font-black underline flex items-center justify-center gap-1 cursor-pointer mt-1"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${diagLoading ? 'animate-spin' : ''}`} />
                      إظهار تفاصيل تشخيص قاعدة البيانات
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>



            {/* One-time Warning Modal */}
            <AnimatePresence>
              {showWarning && (
                <motion.div
                  key="warning-modal"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[200] flex items-center justify-center p-4"
                  style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
                  onClick={dismissWarning}
                >
                  <motion.div
                    initial={{ scale: 0.85, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.85, opacity: 0, y: 20 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="bg-white rounded-3xl p-7 max-w-sm w-full shadow-2xl border border-red-100 relative"
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={dismissWarning}
                      className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 transition-all"
                      aria-label="إغلاق"
                    >
                      <X className="w-4 h-4" />
                    </button>

                    <div className="flex flex-col items-center text-center gap-4">
                      <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center">
                        <AlertTriangle className="w-8 h-8 text-red-600" />
                      </div>
                      <h3 className="text-red-700 font-black text-lg">
                        تنبيه صارم: التطبيق غير قابل للتمسخير
                      </h3>
                      <p className="text-red-600/90 text-sm font-bold leading-relaxed">
                        يُمنع منعاً باتاً القيام بطلبيات وهمية. أي شخص يثبت قيامه بطلبيات
                        متكررة دون استلامها سيتم حظره نهائياً من النظام باسمه ورقم هاتفه،
                        وقد يعرض نفسه للمحاسبة.
                      </p>
                      <button
                        onClick={dismissWarning}
                        className="mt-2 w-full h-12 bg-red-600 hover:bg-red-700 text-white font-black rounded-2xl transition-all shadow-lg shadow-red-200 text-sm"
                      >
                        فهمت، متابعة ←
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {!isRegistering ? (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="space-y-6"
              >
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="mr-4 text-xs font-black text-gray-500 uppercase tracking-widest">اسم المستخدم</Label>
                    <div className="relative group">
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-600 transition-colors">
                        <User className="w-5 h-5" />
                      </div>
                      <Input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="رقم الهاتف أو اسم المستخدم"
                        className="h-14 pr-12 rounded-2xl border-white bg-white/60 focus:bg-white shadow-sm focus:shadow-md transition-all text-right font-bold"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="mr-4 text-xs font-black text-gray-500 uppercase tracking-widest">كلمة المرور</Label>
                    <div className="relative group">
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-600 transition-colors">
                        <Lock className="w-5 h-5" />
                      </div>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="h-14 pr-12 rounded-2xl border-white bg-white/60 focus:bg-white shadow-sm focus:shadow-md transition-all text-right font-bold"
                        required
                      />
                    </div>
                  </div>

                  <div className="bg-green-50/50 p-4 rounded-2xl border border-green-100/50 flex items-center justify-between">
                    <span className="text-xl font-black text-green-700 ml-4">{captcha.q} = ?</span>
                    <Input
                      type="number"
                      value={captchaInput}
                      onChange={e => setCaptchaInput(e.target.value)}
                      placeholder="الإجابة"
                      className="w-24 h-11 text-center font-black rounded-xl border-white bg-white shadow-sm"
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full h-14 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black text-lg shadow-[0_15px_30px_-10px_rgba(21,128,61,0.4)] transition-all hover:-translate-y-1 active:scale-95 border-none">
                    تسجيل الدخول
                  </Button>
                </form>

                <div className="relative py-4">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200/50" /></div>
                  <div className="relative flex justify-center"><span className="bg-white/40 backdrop-blur-sm px-4 text-xs font-black text-gray-400 uppercase tracking-widest">أو</span></div>
                </div>

                <div className="text-center space-y-4">
                  <p className="text-sm text-gray-500 font-bold">زبون جديد؟ انضم لعائلتنا الآن</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsRegistering(true)}
                    className="w-full h-14 rounded-2xl border-2 border-green-600/30 text-green-700 font-black hover:bg-green-50/50 transition-all border-dashed"
                  >
                    <UserPlus className="ml-2 h-5 w-5" />
                    إنشاء حساب زبون جديد
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="register"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.4 }}
              >
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setError('');
                  if (!clientForm.username || !clientForm.password || !clientForm.name || !clientForm.address) {
                    setError('يرجى ملء جميع الحقول');
                    return;
                  }
                  if (parseInt(captchaInput) !== captcha.a) {
                    setError('يرجى حل سؤال الأمان بشكل صحيح');
                    generateCaptcha();
                    return;
                  }
                  try {
                    await registerClient({
                      username: clientForm.username,
                      password: clientForm.password,
                      name: clientForm.name,
                      phone: clientForm.username,
                      address: clientForm.address
                    });
                  } catch (err: any) {
                    console.error('Registration failed:', err);
                    const msg = err.message || '';


                    // Handle both the raw MongoDB error (just in case) and our friendly backend message
                    if (msg.includes('E11000') || msg.includes('duplicate key') || msg.includes('مسجل بالفعل')) {
                      setError('هذا الحساب موجود بالفعل. جاري التحويل لتسجيل الدخول...');
                      setTimeout(() => {
                        setIsRegistering(false);
                        setUsername(clientForm.username); // Pre-fill username
                        setError(''); // Clear error
                      }, 2000);
                    } else {
                      setError(msg || 'فشل التسجيل. يرجى المحاولة مرة أخرى.');
                    }
                  }
                }} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="mr-2 text-xs font-black text-gray-500">الاسم واللقب</Label>
                      <Input value={clientForm.name} onChange={e => setClientForm({ ...clientForm, name: e.target.value })} required className="h-12 rounded-xl border-white bg-white/60 text-right font-bold" placeholder="أدخل اسمك" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="mr-2 text-xs font-black text-gray-500">رقم الهاتف</Label>
                      <Input value={clientForm.username} onChange={e => setClientForm({ ...clientForm, username: e.target.value })} required className="h-12 rounded-xl border-white bg-white/60 text-right font-bold" placeholder="0XXXXX أو +213XXXXX" dir="ltr" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="mr-2 text-xs font-black text-gray-500">العنوان الكامل</Label>
                    <div className="relative">
                      <MapPin className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input value={clientForm.address} onChange={e => setClientForm({ ...clientForm, address: e.target.value })} required className="h-12 pr-10 rounded-xl border-white bg-white/60 text-right font-bold" placeholder="مثلاً: حي 400 مسكن..." />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="mr-2 text-xs font-black text-gray-500">كلمة المرور</Label>
                    <Input type="password" value={clientForm.password} onChange={e => setClientForm({ ...clientForm, password: e.target.value })} required className="h-12 rounded-xl border-white bg-white/60 text-right font-bold" placeholder="أدخل كلمة مرور قوية" />
                  </div>

                  <div className="bg-green-50/50 p-4 rounded-2xl border border-green-100/50 flex items-center justify-between">
                    <span className="text-lg font-black text-green-700">سؤال الأمان: {captcha.q} = ?</span>
                    <Input
                      type="number"
                      value={captchaInput}
                      onChange={e => setCaptchaInput(e.target.value)}
                      placeholder="الحل"
                      className="w-24 h-10 text-center font-black rounded-xl border-white bg-white shadow-sm"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full h-14 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black text-lg shadow-lg border-none mt-2">تأكيد التسجيل</Button>
                  <Button type="button" variant="ghost" onClick={() => setIsRegistering(false)} className="w-full h-12 font-bold text-gray-500 hover:text-green-700">عودة لتسجيل الدخول</Button>
                </form>
              </motion.div>
            )}

            {/* Admin Demo Account - Professional Layout */}

          </CardContent>
        </Card>
      </motion.div>

      {/* Subtle Floating Orbs */}
      <div className="fixed bottom-10 right-10 flex flex-col gap-4 animate-bounce">
        <div className="w-12 h-12 bg-white/80 backdrop-blur-md rounded-full shadow-2xl flex items-center justify-center border border-white">
          <Smartphone className="w-6 h-6 text-green-600" />
        </div>
      </div>

      {/* Diagnostic Modal */}
      <AnimatePresence>
        {showDiag && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-slate-900 border border-slate-800 text-slate-100 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl shadow-black/80 flex flex-col"
              dir="rtl"
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Server className="w-6 h-6 text-green-500" />
                  <h2 className="text-xl font-black font-['Tajawal'] text-white">تشخيص اتصال قاعدة البيانات</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDiag(false)}
                  className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6 text-right">
                {diagMessage && (
                  <div className="p-4 bg-slate-850 border border-slate-750 rounded-2xl text-sm font-bold text-slate-200">
                    {diagMessage}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-1">
                    <span className="text-xs font-bold text-slate-500">حالة الاتصال</span>
                    <div className="flex items-center gap-2 mt-1">
                      {diagStatus?.connected ? (
                        <>
                          <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
                          <span className="text-sm font-black text-green-400">متصل (Connected)</span>
                        </>
                      ) : (
                        <>
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                          <span className="text-sm font-black text-red-400">غير متصل (Not Connected)</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-1">
                    <span className="text-xs font-bold text-slate-500">طريقة الاتصال النشطة</span>
                    <div className="text-sm font-black text-slate-200 mt-1 flex items-center gap-1.5">
                      <Info className="w-4 h-4 text-green-500" />
                      {diagStatus?.mode || 'غير معروف'}
                    </div>
                  </div>
                </div>

                {diagStatus && !diagStatus.connected && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-black text-red-400 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4" />
                      الخطأ الفني المسجل (Database Connection Error)
                    </h3>
                    <div className="bg-slate-950 p-4 rounded-2xl border border-red-950/50 space-y-2 text-left font-mono">
                      <div className="text-xs font-bold text-red-400 whitespace-pre-wrap">
                        {diagStatus.error || 'لا يوجد تفاصيل خطأ مسجلة.'}
                      </div>
                      {diagStatus.stack && (
                        <div className="text-[10px] text-slate-500 max-h-40 overflow-y-auto whitespace-pre overflow-x-auto border-t border-slate-900 pt-2 select-all leading-normal" dir="ltr">
                          {diagStatus.stack}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-3">
                  <h3 className="text-sm font-black text-slate-300">تعليمات حل المشكلة:</h3>
                  <ul className="list-disc list-inside text-xs text-slate-400 space-y-2 leading-relaxed">
                    <li>تأكد من تشغيل البرنامج عبر أيقونة الاختصار <b>GambitaFood</b> على سطح المكتب.</li>
                    <li>تأكد من عدم وجود نافذة أخرى للبرنامج مفتوحة بالخطأ أو عالقة في الخلفية.</li>
                    <li>إذا كنت في وضع الأوفلاين (بدون إنترنت)، فإن تشغيل قاعدة البيانات المحلية لأول مرة قد يستغرق دقيقة واحدة.</li>
                    <li>إذا تكرر الخطأ، اضغط على زر <b>إعادة محاولة الاتصال</b> أدناه ليقوم النظام بإعادة فحص وتدشين الاتصال مجدداً.</li>
                  </ul>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-slate-800 bg-slate-950 flex flex-col md:flex-row gap-3 justify-between">
                <Button
                  type="button"
                  onClick={handleReconnect}
                  disabled={diagLoading}
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700 disabled:bg-slate-800 disabled:text-slate-500 text-white font-black rounded-xl flex items-center justify-center gap-2 border-none shadow-md"
                >
                  <RefreshCw className={`w-4 h-4 ${diagLoading ? 'animate-spin' : ''}`} />
                  إعادة محاولة الاتصال بقاعدة البيانات
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDiag(false)}
                  className="h-12 border-slate-800 text-slate-300 hover:bg-slate-800 hover:text-white font-black rounded-xl px-6"
                >
                  إغلاق
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Smartphone = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
    <path d="M12 18h.01" />
  </svg>
);
